/**
 * Die Zeile eines einzelnen Dokuments, mit optionaler Hervorhebung durch einen Tieflink
 * (Spezifikation § 3.9, Aufgabe 7) - gemeinsam genutzt von `DokumenteTab.tsx` (Bestellexcel-Karte,
 * "Sonstige Dokumente") UND `Checkliste.tsx` (Checklistenzeile).
 *
 * BERICHTIGT (Fixrunde 1 zu Aufgabe 7, Befund W-3): hier stand bis zu dieser Runde ZWEIMAL derselbe
 * Code, einmal je Datei, mit der Begruendung, die Importrichtung `DokumenteTab.tsx ->
 * Checkliste.tsx` solle einseitig bleiben. Das war kein Grund, IN DIESER SCHUBLADE
 * (`Drawer/DocsTab/`) zu duplizieren - ein drittes, gemeinsames Blatt im selben Ordner (wie
 * `UeberschreibDialog.tsx`/`Verteilung.tsx` daneben) kehrt keine Richtung um. Befund W-2 derselben
 * Pruefrunde war die Folge: die `DokumenteTab.tsx`-Kopie war komplett ungetestet und haette beliebig
 * driften koennen, ohne dass ein Test es gemerkt haette - mit EINER Quelle ist das strukturell
 * ausgeschlossen.
 *
 * EIGENE Komponente statt `useRef`/`useEffect` direkt in den `.map()`-Schleifen der Aufrufer
 * ("Sonstige Dokumente" in `DokumenteTab.tsx`, `dateien.map` in `Checkliste.tsx`): die Zahl der
 * Aufrufe schwankt dort mit der Zahl der hochgeladenen Dokumente/Fassungen und haette die
 * Hooks-Regeln verletzt.
 *
 * `data-testid` ist die einzige Verwendung dieses Attributs im ganzen `frontend/src` - fremd
 * gegenueber der sonstigen Hauskonvention (Rolle/`aria-label`). Wortgleich aus dem Aufgabenbrief
 * uebernommen; eine rollenbasierte Alternative war innerhalb dieser Fixrunde nicht in Sicht (der
 * Testzugriff braucht die STABILE `doc_type`-Kennung, keine Rolle traegt die).
 *
 * `data-testid`/Hervorhebung setzen auf `UNIQUE (order_id, doc_type)` (`003_dokumente.sql:51-64`):
 * nur DESHALB ist `docType` je Bestellung eindeutig und trifft hoechstens eine Zeile. Zwei Eintraege
 * desselben `doc_type` an einer Bestellung waeren heute ausgeschlossen, aber nicht durch diese
 * Komponente selbst erzwungen - sie verlaesst sich darauf, ohne es zu pruefen.
 *
 * BERICHTIGT (Fixrunde 2 zu Aufgabe 7, Befund 2): bei reduzierter Bewegung (`prefers-reduced-motion:
 * reduce`, `hervorhebung.css`) laeuft KEINE CSS-Animation - der dortige STATISCHE Ersatzhintergrund
 * gilt dann unbefristet, solange die Klasse `hervorgehoben` steht. Die Klasse selbst haengt bis zu
 * dieser Runde ausschliesslich an `hervorgehoben === docType` - einem Adressfeld, das sich NICHT von
 * selbst aendert. Eine erledigte, gruene Checklistenzeile stand dadurch nach einem Tieflink
 * unbefristet in der Warnfarbe, fuer genau die Nutzergruppe, die von der Animation gar nichts
 * mitbekommt. Derselbe "Zustand, den jemand aufheben muss", den der `forwards`-Fix (Fixrunde 1)
 * bereits fuer die animierte Fassung beseitigt hat - hier per Zeitgeber nachgeholt: `HERVORHEBUNG_
 * DAUER_MS` (muss zur `2s`-Dauer in `hervorhebung.css` passen) raeumt die Klasse nach Ablauf ab,
 * UNABHAENGIG von `prefers-reduced-motion` - ein einziger Mechanismus fuer beide Nutzergruppen statt
 * einer zweiten, `matchMedia`-gestuetzten Fassung derselben Regel.
 */
import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

/**
 * Muss zur Animationsdauer in `hervorhebung.css` (`animation: verweis-hervorhebung 2s ease-out`)
 * passen: bei nicht reduzierter Bewegung faellt die CSS-Animation zu diesem Zeitpunkt ohnehin
 * bereits (fast) auf `background-color: transparent` - der Zeitgeber raeumt die Klasse dort ab, wo
 * die Animation selbst bereits fertig ist, der Uebergang bleibt unauffaellig.
 */
export const HERVORHEBUNG_DAUER_MS = 2000;

export interface DokumentzeileProps {
  docType: unknown;
  hervorgehoben?: string | null;
  className: string;
  children: ReactNode;
}

export function Dokumentzeile({ docType, hervorgehoben, className, children }: DokumentzeileProps) {
  const istHervorgehoben = !!hervorgehoben && String(docType ?? "") === hervorgehoben;
  const ref = useRef<HTMLDivElement>(null);
  // `aktiv` traegt die Hervorhebung ZUSAETZLICH zu `istHervorgehoben` ab - siehe Kopfkommentar,
  // Befund 2. Startwert `true`: faellt `istHervorgehoben` beim ALLERERSTEN Rendern schon auf
  // `true` (der uebliche Fall bei einem frischen Tieflink), soll die Zeile sofort hervorgehoben
  // erscheinen, nicht erst nach diesem Effekt.
  const [aktiv, setAktiv] = useState(true);

  useEffect(() => {
    setAktiv(true);
    if (!istHervorgehoben) return;
    ref.current?.scrollIntoView({ block: "nearest" });
    const zeitgeber = window.setTimeout(() => setAktiv(false), HERVORHEBUNG_DAUER_MS);
    return () => window.clearTimeout(zeitgeber);
    // `scrollIntoView` gibt es in jsdom nicht; die Testhuelle stellt es
    // (`Element.prototype.scrollIntoView = vi.fn()`), damit der Aufruf beweisbar bleibt.
  }, [istHervorgehoben]);

  const zeigeHervorhebung = istHervorgehoben && aktiv;
  return (
    <div
      ref={ref}
      data-testid={`dokument-${String(docType ?? "")}`}
      className={zeigeHervorhebung ? `${className} hervorgehoben` : className}
    >
      {children}
    </div>
  );
}
